<template>
  <v-container id="run-environment-desktop" fluid>
    <v-container class="text-center" v-if="loading">
      <v-progress-circular class="loading" indeterminate color="#881E98"></v-progress-circular>
    </v-container>

    <v-form @submit.prevent="onSubmit" v-else>
      <v-card id="environment-card" class="mx-auto mt-2" max-width="100%" outlined>
        <v-list-item>
          <v-list-item-content>
            <v-row class="ml-1">
              <div class="overline mb-4 template-name">{{ templateName }}</div>
              <v-chip class="pa-2 ml-3 info-chip" outlined small>{{ runOn }}</v-chip>
            </v-row>
            <v-list-item-title class="mb-1" id="run-environment-title">
              <v-icon class="title-icon">far fa-cloud-upload-alt</v-icon>Create new environment
            </v-list-item-title>
            <span>
              <hr class="underscore" />
            </span>
            <v-container fluid>
              <v-text-field
                label="Environment name"
                class="environment-name"
                v-model="environment.name"
                :error="errors && !$v.environment.name.required && !$v.environment.name.maxLength"
                maxlength="50"
              ></v-text-field>

              <Providers
                class="providers"
                :class="{'providers-non-selected': !environment.selectedProvider}"
                :errors="[errors, $v.environment.selectedProvider.required]"
                :templateId="templateId"
                :providers="parsedProviders"
                @selectedProvider="onSelectedProvider"
                @selectedProviderId="onSelectedProviderId"
              />
              <!-- <Sizes
                  :errors="[errors, $v.environment.selectedSize.required]"
                  @selectedSize="onSelectedSize"
              />-->

              <v-text-field
                v-if="environment.selectedProvider"
                label="Environment domain prefix"
                class="environment-domain-prefix"
                v-model="environment.domainPrefix"
                :error="errors && !$v.environment.domainPrefix.required"
                :suffix="`.${environment.selectedProvider}.platform.ttpsc.cloud`"
                :rules="[friendlyUrl]"
                :maxlength="63"
                persistent-hint
              ></v-text-field>

              <v-select
                class="region-select"
                hint="Promote better performance by choosing a location close to your users"
                persistent-hint
                :items="setRegionsPerProvider"
                item-text="item"
                item-value="value"
                :menu-props="{offsetY: true }"
                label="Region"
                append-icon="fas fa-caret-down"
                v-model="environment.region"
                :error="errors && !$v.environment.region.required"
                v-if="environment.selectedProvider"
              ></v-select>

              <Sizes
                v-if="runOn === 'VM'"
                class="sizes"
                :errors="[errors, $v.environment.selectedSize.required]"
                @selectedSize="onSelectedSize"
                :sizes="vmSizes"
              />

              <SSH v-if="runOn === 'VM' && regionsPerProvider.parsedOutput[0].OS === 'Linux'" :sshKeys="SSHKeys" @selectedSSHKey="onSelectedSSHKey" />

              <Scheduler
                :errors="[errors, $v.environment]"
                @selectedScheduler="onSelectedScheduler"
                @selectedTimezone="onSelectedTimezone"
                @selectedHours="onSelectedHours"
                @schedulerSwitch="onSchedulerSwitch"
              />

              <v-card-actions
                class="actions"
                :class="{'actions': environment.isSchedulerEnabled, 'actions-scheduler-disabled': !environment.isSchedulerEnabled }"
              >
                <v-container fluid>
                  <v-layout wrap align-center class="card-actions">
                    <v-flex xs6 class="text-center">
                      <v-btn class="text-none" id="cancel-button" text @click="cancel()">Cancel</v-btn>
                    </v-flex>
                    <v-flex xs6 class="text-left">
                      <v-btn
                        class="text-none"
                        type="submit"
                        id="run-button-style"
                        :disabled="
                          $v.$invalid || 
                          environment.isSchedulerEnabled &&
                          environment.scheduler.length === 0 || 
                          domainPrefixError ||
                          creatingEnvironment"
                      >Run new environment</v-btn>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-actions>
            </v-container>
          </v-list-item-content>
        </v-list-item>
      </v-card>

      <!-- <div class="estimated-cost-container">
        <p class="estimated-cost-paragraph">Estimated cost</p>
        <p class="estimated-amount-paragraph">$30</p>
        <p class="slash">/ day</p>
      </div>-->
    </v-form>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Scheduler from "./Scheduler";
import Providers from "./Providers";
import Sizes from "./Sizes";
import SSH from "./SSH";
// import Port from "./Port";
import { validationMixin } from "vuelidate";

import { required, requiredIf, maxLength } from "vuelidate/lib/validators";

export default {
  data() {
    return {
      vmSwitch: false,
      loading: false,
      creatingEnvironment: false,
      regions: ["eu-west-1", "us-west-1", "us-east-1"],
      parsedProviders: [],
      templateName: "",
      runOn: "",
      environment: {
        selectedProvider: null,
        selectedProviderId: null,
        selectedSize: "",
        name: "",
        region: "",
        selectedTimezone: "",
        scheduler: [],
        // for validation
        startHour: "",
        stopHour: "",
        day: "",
        domainPrefix: "",
        isSchedulerEnabled: false,
        sshKeyId: "",
        port: null
      },
      templateId: "",
      errors: false,
      domainPrefixError: false,
      vmSizes: [],
      isPortValid: true,
    };
  },
  components: { Scheduler, Providers, Sizes, SSH/*, Port*/ },
  mixins: [validationMixin],
  validations: {
    environment: {
      selectedProvider: { required },
      name: { required, maxLength: maxLength(50) },
      region: { required },
      selectedTimezone: {
        required: requiredIf(function() {
          return this.environment.isSchedulerEnabled;
        })
      },
      day: {
        required: requiredIf(function() {
          return this.environment.isSchedulerEnabled;
        })
      },
      startHour: {
        required: requiredIf(function() {
          return this.environment.isSchedulerEnabled;
        }),
        validateHours() {
          const regex = /^([0-9]|1[0-9]|2[0-3])$/;
          return this.environment.isSchedulerEnabled
            ? this.environment.startHour < this.environment.stopHour &&
                this.environment.startHour.toString().match(regex) &&
                this.environment.stopHour.toString().match(regex)
            : true;
        }
      },
      stopHour: {
        required: requiredIf(function() {
          return this.environment.isSchedulerEnabled;
        }),
        validateHours() {
          const regex = /^([0-9]|1[0-9]|2[0-3])$/;
          return this.isSchedulerEnabled
            ? this.environment.startHour < this.environment.stopHour &&
                this.environment.startHour.toString().match(regex) &&
                this.environment.stopHour.toString().match(regex)
            : true;
        }
      },
      domainPrefix: { required, maxLength: maxLength(63) },
      selectedSize: {
        required: requiredIf(function() {
          return this.runOn === "VM";
        })
      },
      // port: {
      //   required: requiredIf(function() {
      //     return this.runOn === "VM";
      //   }),
      // }
      // sshKeyId: {
      //   required: requiredIf(function() {
      //     return this.runOn === "VM" && this.regionsPerProvider.parsedOutput[0].OS === 'Linux';
      //   })
      // }
    }
  },
  computed: {
    ...mapGetters(["regionsPerProvider", "templates", "sizes", "SSHKeys", "privateTemplates"]),
    setRegionsPerProvider() {
      const regions = this.regionsPerProvider.parsedOutput.filter(
        provider =>
          provider.ServiceProviderName === this.environment.selectedProvider
      );

      return regions.map(region => ({
        item: region.DisplayName,
        value: region.ServiceRegionId
      }));
    }
  },
  methods: {
    ...mapActions([
      "runEnvironment",
      "getTemplateRegionInfo",
      "getTemplateVmSizes",
      "getTemplates",
      "getSSHKeys",
      "getUserTemplates"
    ]),
    onSelectedProvider(name) {
      const { selectedProvider } = this.environment;
      name === selectedProvider
        ? (this.environment.selectedProvider = null)
        : (this.environment.selectedProvider = name);
    },
    onSelectedProviderId(id) {
      const { selectedProviderId } = this.environment;
      id === selectedProviderId
        ? (this.environment.selectedProviderId = null)
        : (this.environment.selectedProviderId = id);
    },
    onSelectedSize(size) {
      const { selectedSize } = this.environment;
      this.environment.selectedSize = size === selectedSize ? null : size;
    },
    onSelectedScheduler(scheduler) {
      if (scheduler.length === 0) {
        return (this.environment.scheduler = []);
      }
      this.environment.scheduler = scheduler;
      // for validation
      this.environment.day = scheduler[0].day;
    },
    onSelectedHours(startHour, stopHour, hourPostfixFrom, hourPostfixTo) {
      this.environment.startHour = this.convertTime(startHour, hourPostfixFrom);
      this.environment.stopHour = this.convertTime(stopHour, hourPostfixTo);
    },
    convertTime(hour, postfix) {
      const parsedHour = parseInt(hour, 10);
      let convertedHour = "";
      if (postfix === "PM" && parsedHour >= 1 && parsedHour <= 12) {
        if (parsedHour === 12) convertedHour = parsedHour;
        else convertedHour = 12 + parsedHour;
      } else if (postfix === "AM" && parsedHour >= 1 && parsedHour <= 12) {
        if (parsedHour === 12) convertedHour = 0;
        else convertedHour = parsedHour;
      }
      return convertedHour;
    },
    onSelectedTimezone(timezone) {
      this.environment.selectedTimezone = timezone;
    },
    onSelectedSSHKey(key) {
      this.environment.sshKeyId = key;
    },
    checkForm() {
      if (this.$v.$invalid || this.$v.$error) {
        this.errors = true;
      } else {
        this.errors = false;
      }
    },
    cancel() {
      this.$router.back();
    },
    onSchedulerSwitch(data) {
      this.environment.isSchedulerEnabled = data;
    },
    async onSubmit() {
      this.creatingEnvironment = true;
      this.checkForm();

      const data = {
        templateId: this.templateId,
        regionId: this.environment.region,
        providers: [this.environment.selectedProviderId],
        environmentName: this.environment.name,
        domainPrefix: this.environment.domainPrefix,
        ...(this.environment.startHour && {
          startHour: `${this.environment.startHour}`
        }),
        ...(this.environment.stopHour && {
          stopHour: `${this.environment.stopHour}`
        }),
        // scheduler: this.environment.isSchedulerEnabled
        //   ? this.environment.scheduler.map(schedule => schedule.day)
        //   : [],
        schedulerEnabled: this.environment.isSchedulerEnabled,
        ...(this.environment.scheduler.length > 0 && {
          scheduler: this.environment.scheduler.map(schedule => schedule.day)
        }),
        ...(this.environment.selectedTimezone && {
          timezone: this.environment.selectedTimezone
        }),
        ...(this.environment.selectedSize && {
          instanceTypeId: this.environment.selectedSize
        }),
        ...(this.environment.sshKeyId && {
          sshKeyId: this.environment.sshKeyId
        }),
        port: null,
      };

      if (!this.errors) {
        const { error, result } = await this.runEnvironment(data);
        if (!error) {
          return this.$router.push("/environments");
        }

        this.creatingEnvironment = false;
      } else {
        console.log("validation error");
      }
    },
    friendlyUrl(value) {
      if (/^(?![0-9]+$)(?!.*-$)(?!-)[a-zA-Z0-9-]{1,63}$/.test(value)) {
        this.domainPrefixError = false;
        return true;
      }
      this.domainPrefixError = true;
      return "Domain prefix must contain only alphanumeric characters, can't be empty and could have max 63 characters!";
    }
  },
  async created() {
    this.loading = true;
    this.templateId = Number.parseInt(this.$route.params.id);

    if (!this.templates.length) await this.getTemplates();

    let template;

    if(this.templates.length > 0) {
      template = this.templates.filter(template => template.Id === this.templateId);
      if(template.length > 0) {
        this.templateName = template[0].TemplateName;
      }
      else {
        await this.getUserTemplates();
        this.templateName = this.privateTemplates.filter(template => template.Id === this.templateId)[0].TemplateName;
      }
    }

    const { error, result } = await this.getTemplateRegionInfo(this.templateId);

    await this.getTemplateVmSizes(this.templateId);
    this.vmSizes = this.sizes.map(size => ({ ...size, selected: false }));

    // redirect to the templates page if there are no providers for the specific template
    // it can mean this template doesn't exist or there are no providers for this template.
    // in both cases, you should redirect to the templates page.
    if (result.providers.length === 0 || error) {
      const route = template.length > 0 ? "/templates" : "/my-templates";  
      return this.$router.push(route);
    }

    this.parsedProviders = this.regionsPerProvider.providers.map(
      (provider, index) => ({
        id: this.regionsPerProvider.parsedOutput.filter(
          env => env.ServiceProviderName == provider
        )[0].ServiceProviderId,
        name: provider
      })
    );

    if (this.parsedProviders.length == 1) {
      this.onSelectedProvider(this.parsedProviders[0]['name']);
      this.onSelectedProviderId(this.parsedProviders[0]['id']);
      this.parsedProviders[0]['selected'] = true;
    }

    this.runOn =
      template.length > 0
        ? template[0].Type
        : this.privateTemplates.filter(template => template.Id === this.templateId)[0].Type;

    await this.getSSHKeys();
    this.loading = false;
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css?family=Montserrat&display=swap");

@media (min-width: 960px) {
  #run-environment-desktop {
    max-width: 40%;
    margin-top: 120px;
  }

  #environment-card {
    border: 0;
  }
}

@media (min-width: 600px) {
  #run-environment-title {
    font-size: 0.5em !important;
  }
}

#run-environment-title {
  font-family: "Montserrat" !important;
  font-weight: bold;
  font-size: 1.7em !important;
}

.labels {
  font: 14px "Montserrat" !important;
  margin-top: 5px;
}

#run-button-style {
  font: 1em "Montserrat" !important;
  color: #fff;
  background: #6db42c;
  border-radius: 138px;
  width: 234px;
  height: 46px;
}

.cloud-title {
  padding-bottom: 20px;
  padding-top: 20px;
  font: Bold 16px/19px "Montserrat" !important;
}

.environment-name {
  height: 40px;
}

.environment-domain-prefix {
  height: 40px;
  margin-top: 50px;
}

.scheduler {
  background: #f9f9f9;
}
form {
  font-family: "Montserrat" !important;
}

.title-icon {
  color: black !important;
  margin-right: 20px;
  font: 1.7em "Font Awesome 5 Pro" !important;
}

#cancel-button {
  font: 1em "Montserrat" !important;
  border-radius: 138px;
  width: 234px;
  height: 46px;
}
.underscore {
  border: 1.5px solid #6db42c;
  border-radius: 1px;
  width: 100px;
  margin-right: 100%;
  margin-top: 10px;
  margin-bottom: 20px;
  background-color: #6db42c;
}

.providers {
  margin-top: 15px;
}

.providers-non-selected {
  margin-bottom: 55px;
}

.region-select {
  margin-top: 30px;
  margin-bottom: 60px;
}

.actions {
  margin-top: 60px;
  height: 130px;
  width: 100%;
}

.actions-scheduler-disabled {
  margin-top: 10px;
  height: 130px;
  width: 100%;
}

.estimated-cost-container {
  text-align: center;
  margin-top: 60px;
}

.estimated-cost-paragraph {
  font: bold 24px/29px "Montserrat";
  display: inline;
  margin-right: 20px;
}

.estimated-amount-paragraph {
  display: inline;
  font: bold 24px/29px "Montserrat";
  color: #d0398b;
  margin-right: 5px;
}

.slash {
  color: #bbbbbb;
  display: inline;
  font: 24px/29px "Montserrat";
}

.loading {
  margin-top: 24px;
}

.template-name {
  font-size: 0.9em !important;
  font-family: "Montserrat";
}

.sizes {
  margin-bottom: 6vh;
}

.info-chip {
  color: #881e98;
  font-weight: bold;
  font-size: 0.7rem;
  border-color: #881e98 !important;
  height: 16px;
  margin-top: -1px;
}
</style>
